import { useState, useEffect, useCallback, useRef } from "react";
import { FirestoreNotificationRepository } from "@/infrastructure/firestore/FirestoreNotificationRepository";
import { useAuth } from "@/app/providers/AuthProvider";
import type { Notification } from "@/domain/notification/notification.types";
import { playNotificationAlert } from "@/features/notifications/services/notificationSound";
import { toast } from "sonner";

const repo = new FirestoreNotificationRepository();

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const notificationsRef = useRef<Notification[]>([]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    notificationsRef.current = [];
    let initialized = false;
    const unsubscribe = repo.subscribeToNotifications(user.uid, (data) => {
      if (initialized) {
        const previousIds = new Set(notificationsRef.current.map((notification) => notification.id));
        const hasAlert = data.some((notification) =>
          !previousIds.has(notification.id) &&
          ["TASK_ASSIGNED", "TASK_REASSIGNED", "TASK_DUE_SOON", "TASK_OVERDUE", "COMPLIANCE_REMINDER"].includes(notification.type),
        );
        if (hasAlert) void playNotificationAlert();
      }
      initialized = true;
      notificationsRef.current = data;
      setNotifications(data);
      setLoading(false);
    }, async (error) => {
      console.error("Notification subscription failed:", error);
      try {
        const data = await repo.getNotifications(user.uid);
        notificationsRef.current = data;
        setNotifications(data);
      } catch (fallbackError) {
        toast.error("Inbox is temporarily unavailable");
        console.error("Notification fallback load failed:", fallbackError);
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, [user]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    await repo.markAsRead(notificationId);
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    await repo.markAllAsRead(user.uid);
  }, [user]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    await repo.deleteNotification(notificationId);
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
}
